import type { ChannelDto } from '@gr/core/distribution/channels/dto';
import { StepPlacement, type SaveSchemeRequestDto, type SchemeDto } from '@gr/core/distribution/scheduling/dto';

/** Шаг в форме: задержка — в часах (так её вводят), в запросе — в минутах. */
export interface StepRow {
  channelId: string;
  delayHours: string;
  placement: StepPlacement;
}

export interface SchemeValues {
  name: string;
  steps: StepRow[];
}

/** Задержка второго и следующих шагов по умолчанию — полсуток. */
const DEFAULT_DELAY_HOURS = '12';

export function schemeFormValues(scheme: SchemeDto | null, channels: readonly Pick<ChannelDto, 'id'>[]): SchemeValues {
  if (!scheme) return { name: '', steps: [{ channelId: channels[0]?.id ?? '', delayHours: '0', placement: StepPlacement.Slot }] };
  return { name: scheme.name, steps: scheme.steps.map((s) => ({ channelId: s.channelId, delayHours: String(s.delayMinutes / 60), placement: s.placement })) };
}

/** Следующий шаг — в первый канал, которого ещё нет в схеме. */
export function nextStep(steps: readonly StepRow[], channels: readonly Pick<ChannelDto, 'id'>[]): StepRow {
  const free = channels.find((c) => !steps.some((s) => s.channelId === c.id));
  return { channelId: free?.id ?? '', delayHours: DEFAULT_DELAY_HOURS, placement: StepPlacement.Slot };
}

export function toSchemeRequest(values: SchemeValues): SaveSchemeRequestDto {
  return {
    name: values.name,
    steps: values.steps.map((step, index) => ({
      channelId: step.channelId,
      // У первого шага задержки нет; дробные часы допустимы, отрицательные — нет.
      delayMinutes: index === 0 ? 0 : Math.max(0, Math.round(Number(step.delayHours.replace(',', '.') || 0) * 60)),
      placement: step.placement,
    })),
  };
}

/** Схема словами: «Telegram → Instagram через 24 ч». */
export function schemeSummary(scheme: Pick<SchemeDto, 'steps'>, channels: readonly Pick<ChannelDto, 'id' | 'name'>[], t: (key: string, params?: Record<string, string | number>) => string): string {
  return scheme.steps
    .map((step, index) => {
      const channel = channels.find((c) => c.id === step.channelId)?.name ?? '?';
      return index === 0 ? t('scheduling.schemes.summaryFirst', { channel }) : t('scheduling.schemes.summaryNext', { channel, hours: Math.round((step.delayMinutes / 60) * 10) / 10 });
    })
    .join(' → ');
}
